import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import Loader from '@/shared/components/Loader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip"
import retryIcon from '@/images/SVG/retryIcon.svg'
import commentIcon from "@/images/SVG/comment.svg"
import { useTranslation } from 'react-i18next';
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'


const CustomTable = ({ selectedTabReports, columns, onClickFunction, handleRetry }) => {
  const [processingRows, setProcessingRows] = useState({});
  const { t } = useTranslation();
  const { nonAudioTool } = useCurrentTool();

  const getColumnWidth = (column) => {
    if (column.accessor === 'row_number')
      return 'w-[130px] min-w-[130px]';
    else 
      return 'w-[220px] min-w-[220px]';
  };

  const getItemState = ({ status, id, error, report_fetched, comment }) => {
    const state = (status === 'completed' && !report_fetched) ? 'failed' : status;
    const isRowLoading = processingRows[id];

    if (state === 'retry' || isRowLoading) {
      return (
        <div className="text-[14px] font-SuisseIntlLight font-normal flex items-center gap-1">
          <Loader />
        </div>
      )
    }
    else if (state === 'failed' && (["Failed to upload file", "Failed to fetch signed url"]).includes(error)) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <img src={retryIcon} alt="retry icon" className='max-w-none w-4 h-4 opacity-30' />
            </TooltipTrigger>
            <TooltipContent side="left" align='end' className='flex flex-wrap max-w-[300px] p-2'>
              {nonAudioTool ? t('errors.docMissing') : t('errors.audioMissing')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    else if (state === 'failed') {
      return (
        <div>
          <button className="block" 
            onClick={() => {
              setProcessingRows((prev) => ({ ...prev, [id]: true })); 
              handleRetry({ id });
              setTimeout(() => setProcessingRows((prev) => ({ ...prev, [id]: false })), 2000);
            }}
          >
            <img src={retryIcon} alt="retry icon" className='max-w-none w-4 h-4' />
          </button>
        </div>
      )
    }
    else if (!!comment) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger><img src={commentIcon} alt="comment icon" className='max-w-none w-4 h-4' /></TooltipTrigger>
            <TooltipContent side="left" align='end' className='flex flex-wrap max-w-[300px] p-2'>
              <p>{comment}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
  }

  return (
    <div className='w-full border rounded-[20px] overflow-hidden'>
      <Table className='table-fixed'>
        <TableHeader>
          <TableRow>
            {columns.map((head, index) => (
              <TableHead
                className={`capitalize text-[#71717A] font-SuisseIntlLight font-semibold lg:px-4 px-6 ${getColumnWidth(head)}`}
                key={index}
              >
                {head.Header}
              </TableHead>
            ))}
            <TableHead className="capitalize text-[#71717A] font-SuisseIntlLight font-semibold lg:px-4 px-6 w-[60px] min-w-[60px]">
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {selectedTabReports.map((report, index) => {
            const isDisabled = (report.status === 'completed' && !report.report_fetched);
            return (
              <TableRow
                key={index}
                className={`relative ${(isDisabled || (report.status === 'processing')) && 'cursor-not-allowed'}`}
              >
                {columns.map((column) => (
                  <TableCell key={column.accessor} className={`p-0 ${getColumnWidth(column)}`}>
                    <div
                      className={`w-full h-full lg:px-4 px-6 p-4 text-left ${(isDisabled || (report.status === 'processing')) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={() => !(isDisabled || (report.status === 'processing')) && onClickFunction(report.id)}
                      disabled={isDisabled || (report.status === 'processing')}
                    >
                      {/* 1. If the column has value in report.fields object, then display the value.
                          2. If the column has a Cell function, check if its status or not.
                          3. If status is 'completed' and report_fetched is false, then pass the value to Cell function as 'failed'.
                          4. If not, then pass the value to the Cell function.
                          5. If none of the above, then display the value from report object.
                      */}
                      {report.fields?.[column.accessor] !== undefined ? (
                        report.fields[column.accessor]
                      ) : (
                        column.Cell
                          ? column.accessor === 'status' 
                          ? column.Cell({ value: isDisabled ? 'failed' : report[column.accessor] })
                          : column.Cell({ value: report[column.accessor], report })
                          : report[column.accessor]
                      )}
                    </div>
                  </TableCell>
                ))}
                <TableCell className="p-0">
                  {getItemState(report)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CustomTable;
